/**
 * Phase F4 — Admin Distribution Approval Queue
 * ---------------------------------------------
 * Surfaces the Phase 7 admin distribution pipeline.
 *
 * Endpoints used:
 *   GET  /api/admin/distributions/queue                       — counts + buckets per status
 *   POST /api/distributions/{distribution_id}/approve         — DRAFT/GENERATED → APPROVED
 *   POST /api/distributions/{distribution_id}/reject          — → REJECTED
 *   POST /api/distributions/{distribution_id}/cancel          — → CANCELLED
 *   POST /api/distributions/{distribution_id}/ready-for-payout — APPROVED → READY_FOR_PAYOUT
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, FileText, CheckCircle, Wallet, XCircle, Ban, Edit3,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'draft', label: 'Draft', Icon: Edit3, color: theme.colors.text.muted },
  { key: 'generated', label: 'Generated', Icon: FileText, color: theme.colors.status.info },
  { key: 'approved', label: 'Approved', Icon: CheckCircle, color: theme.colors.accent.teal },
  { key: 'ready_for_payout', label: 'Ready For Payout', Icon: Wallet, color: theme.colors.status.success },
  { key: 'cancelled', label: 'Cancelled', Icon: Ban, color: theme.colors.text.muted },
  { key: 'rejected', label: 'Rejected', Icon: XCircle, color: theme.colors.status.error },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtMoney(n, cur = 'USD') {
  return `${(Number(n) || 0).toFixed(2)} ${cur}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function AdminDistributionApprovalQueue() {
  const [activeTab, setActiveTab] = useState('generated');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/distributions/queue`, auth);
      setData(res.data || null);
    } catch (e) {
      toast.error('Failed to load distribution queue');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const buckets = data?.buckets || {};
  const counts = data?.counts || {};
  const rows = buckets[activeTab] || [];

  const doAction = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/distributions/${id}/${action}`, {}, auth);
      toast.success(`Distribution ${action.replace('-', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-distribution-queue-page" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <DollarSign size={26} color={theme.colors.primary} />
          Distribution Approval Queue
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Review distributions, approve, reject, or move to ready-for-payout.
        </p>
      </div>

      {/* Tabs */}
      <div data-testid="distribution-queue-tabs" style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`dist-queue-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div data-testid="dist-queue-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="dist-queue-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No distributions in this bucket.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'ID', 'Total', 'Status', 'Generated', 'Actions'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const status = d.status;
                const canApprove = status === 'DRAFT' || status === 'GENERATED';
                const canReject  = status === 'GENERATED' || status === 'APPROVED';
                const canCancel  = ['DRAFT', 'GENERATED', 'APPROVED'].includes(status);
                const canReady   = status === 'APPROVED';
                return (
                  <tr key={d.id} data-testid={`dist-queue-row-${d.id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {d.portfolio_name || d.portfolio_id}
                    </td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: theme.colors.text.secondary }}>
                      {(d.id || '').slice(0, 10)}…
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 700 }}>
                      {fmtMoney(d.total_amount || d.distribution_amount, d.currency)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 999,
                        background: `${theme.colors.primary}15`, color: theme.colors.primary,
                        fontSize: 11, fontWeight: 700,
                      }}>{status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{fmtDate(d.generated_at)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {canApprove && (
                          <button
                            data-testid={`dist-approve-${d.id}`} disabled={busyId === d.id}
                            onClick={() => doAction(d.id, 'approve')}
                            style={{
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: 'none', background: theme.colors.status.success,
                              color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                            }}>Approve</button>
                        )}
                        {canReady && (
                          <button
                            data-testid={`dist-ready-${d.id}`} disabled={busyId === d.id}
                            onClick={() => doAction(d.id, 'ready-for-payout')}
                            style={{
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: 'none', background: theme.colors.primary,
                              color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                            }}>Ready For Payout</button>
                        )}
                        {canReject && (
                          <button
                            data-testid={`dist-reject-${d.id}`} disabled={busyId === d.id}
                            onClick={() => doAction(d.id, 'reject')}
                            style={{
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                              color: theme.colors.status.error, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                            }}>Reject</button>
                        )}
                        {canCancel && (
                          <button
                            data-testid={`dist-cancel-${d.id}`} disabled={busyId === d.id}
                            onClick={() => doAction(d.id, 'cancel')}
                            style={{
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                              color: theme.colors.text.muted, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                            }}>Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
